import { type KeyboardEvent, type ReactNode, useMemo } from 'react';
import { FaChevronRight, FaExclamationTriangle, FaSortAmountDown } from 'react-icons/fa';
import StatusBadge from '@/components/UI/StatusBadge';
import Tooltip from '@/components/UI/Tooltip';
import {
    type AlphaAtlasOutcomeKind,
    type AlphaAtlasReportRow,
    type AlphaAtlasSortBy,
    type AlphaAtlasWindowRole,
    alphaAtlasDirectionLabel,
    CONDITION_KIND_META,
    describeConcentration,
    describeEvidenceBadge,
    describeWindowConsistency,
    EVIDENCE_STATE_META,
    SORT_META,
    validationTwinOf,
    WINDOW_CONSISTENCY_META,
    WINDOW_ROLE_META
} from './AlphaAtlasReportState';
import {
    describeCombination,
    formatCount,
    formatHorizon,
    formatPercentMicros,
    formatRatio,
    TONE_TEXT_CLASS,
    toneForMicros
} from './alphaAtlasFormat';

interface AlphaAtlasResultsTableProps {
    rows: AlphaAtlasReportRow[];
    /** First load with nothing to show yet: renders skeleton rows */
    isLoading: boolean;
    /** A newer page is loading while the previous rows stay visible */
    isRefreshing: boolean;
    /** The window the rows were read from; its label heads the primary metric group */
    windowRole: AlphaAtlasWindowRole;
    outcomeKind: AlphaAtlasOutcomeKind;
    sortBy: AlphaAtlasSortBy;
    onSortChange: (sortBy: AlphaAtlasSortBy) => void;
    selectedRowFingerprint: string | null;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}

const TH = 'px-3 py-2 text-xs font-medium uppercase tracking-wider text-zinc-400 whitespace-nowrap';
const TD = 'px-3 py-2.5 align-top text-sm';
const GROUP_BORDER = 'border-l border-zinc-800';
const SKELETON_ROWS = 8;

const HeaderLabel = ({ label, description }: { label: string; description?: string }) =>
    description ? (
        <Tooltip content={description}>
            <span className="cursor-help underline decoration-dotted decoration-zinc-600 underline-offset-4">{label}</span>
        </Tooltip>
    ) : (
        <span>{label}</span>
    );

const SortableHeader = ({
    sortKey,
    label,
    active,
    onSortChange,
    rowSpan,
    className = ''
}: {
    sortKey: AlphaAtlasSortBy;
    label: string;
    active: boolean;
    onSortChange: (sortBy: AlphaAtlasSortBy) => void;
    rowSpan?: number;
    className?: string;
}) => (
    <th aria-sort={active ? 'descending' : undefined} className={`${TH} text-right ${className}`} rowSpan={rowSpan} scope="col">
        <button
            className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-white ${active ? 'text-white' : ''}`}
            title={SORT_META[sortKey].description}
            type="button"
            onClick={() => onSortChange(sortKey)}
        >
            {label}
            {active && <FaSortAmountDown aria-label="Ranked by this column" className="h-3 w-3 text-cyan-400" />}
        </button>
    </th>
);

const Signed = ({ micros, className = '' }: { micros: number | null; className?: string }) => (
    <span className={`tabular-nums ${TONE_TEXT_CLASS[toneForMicros(micros)]} ${className}`}>
        {formatPercentMicros(micros, { signed: true })}
    </span>
);

const Blank = ({ title }: { title: string }) => (
    <span className="text-zinc-600" title={title}>
        —
    </span>
);

/** Tiny horizontal range of the 95% interval on a shared symmetric scale, with a zero marker. */
const IntervalBar = ({ lower, upper, scale }: { lower: number | null; upper: number | null; scale: number }) => {
    if (lower === null || upper === null) return null;
    const toPercent = (micros: number) => 50 + (Math.max(-scale, Math.min(scale, micros)) / scale) * 50;
    const left = toPercent(lower);
    const width = Math.max(1, toPercent(upper) - left);
    const tone = lower > 0 ? 'bg-green-500/70' : upper < 0 ? 'bg-red-500/70' : 'bg-zinc-500/70';
    return (
        <span aria-hidden="true" className="relative mt-1 block h-1.5 w-24 rounded bg-zinc-800">
            <span className="absolute inset-y-0 w-px bg-zinc-600" style={{ left: '50%' }} />
            <span className={`absolute inset-y-0 rounded ${tone}`} style={{ left: `${left}%`, width: `${width}%` }} />
        </span>
    );
};

const SkeletonRow = ({ columns }: { columns: number }) => (
    <tr>
        {Array.from({ length: columns }, (_, index) => (
            <td className={TD} key={index}>
                <div className="h-3 w-full max-w-[9rem] animate-pulse rounded bg-zinc-800" />
                <div className="mt-2 h-2 w-2/3 max-w-[6rem] animate-pulse rounded bg-zinc-800/70" />
            </td>
        ))}
    </tr>
);

const AlphaAtlasResultsTable = ({
    rows,
    isLoading,
    isRefreshing,
    windowRole,
    outcomeKind,
    sortBy,
    onSortChange,
    selectedRowFingerprint,
    onSelectRow
}: AlphaAtlasResultsTableProps) => {
    const intervalScale = useMemo(
        () => Math.max(1, ...rows.flatMap((row) => [Math.abs(row.ciLowerMicros ?? 0), Math.abs(row.ciUpperMicros ?? 0)])),
        [rows]
    );
    // Reading the validation window itself: its twin columns would repeat the primary ones.
    const showValidation = windowRole !== 'selectionValidation';
    const columnCount = showValidation ? 14 : 9;

    const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: AlphaAtlasReportRow) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelectRow(row);
        }
    };

    const cell = (content: ReactNode, extra = '') => <td className={`${TD} ${extra}`}>{content}</td>;

    return (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
            <table className={`w-full text-left ${showValidation ? 'min-w-[1680px]' : 'min-w-[1120px]'}`}>
                <thead className="bg-[#131416]">
                    <tr className="border-b border-zinc-800">
                        <th className={TH} rowSpan={2} scope="col">
                            <HeaderLabel
                                description="Trigger event, optionally combined with a context state that was present when it fired."
                                label="Hypothesis"
                            />
                        </th>
                        <th className={TH} rowSpan={2} scope="col">
                            Where
                        </th>
                        <SortableHeader
                            active={sortBy === 'support'}
                            label="Support"
                            rowSpan={2}
                            sortKey="support"
                            onSortChange={onSortChange}
                        />
                        <th className={`${TH} ${GROUP_BORDER} text-center`} colSpan={4} scope="colgroup">
                            <HeaderLabel
                                description={WINDOW_ROLE_META[windowRole].description}
                                label={WINDOW_ROLE_META[windowRole].label}
                            />
                        </th>
                        {showValidation && (
                            <>
                                <th className={`${TH} ${GROUP_BORDER} text-center`} colSpan={3} scope="colgroup">
                                    <HeaderLabel
                                        description={WINDOW_ROLE_META.selectionValidation.description}
                                        label="Selection validation (walk-forward)"
                                    />
                                </th>
                                <th className={`${TH} ${GROUP_BORDER} text-center`} colSpan={2} scope="colgroup">
                                    <HeaderLabel
                                        description="Whether the edge vs random entry is positive in both controlled windows. The strongest robustness signal available without revealing verification evidence."
                                        label="Across windows"
                                    />
                                </th>
                            </>
                        )}
                        <th className={`${TH} ${GROUP_BORDER}`} rowSpan={2} scope="col">
                            <HeaderLabel
                                description="Where the result stands in the controlled selection and verification process."
                                label="Evidence"
                            />
                        </th>
                        <th className={TH} rowSpan={2} scope="col">
                            <span className="sr-only">Open details</span>
                        </th>
                    </tr>
                    <tr>
                        <SortableHeader
                            active={sortBy === 'netReturn'}
                            className={GROUP_BORDER}
                            label="Net return"
                            sortKey="netReturn"
                            onSortChange={onSortChange}
                        />
                        <SortableHeader
                            active={sortBy === 'randomEdge'}
                            label="Edge vs random"
                            sortKey="randomEdge"
                            onSortChange={onSortChange}
                        />
                        <SortableHeader active={sortBy === 'lift'} label="Lift vs trigger" sortKey="lift" onSortChange={onSortChange} />
                        <SortableHeader
                            active={sortBy === 'liftVsContext'}
                            label="Lift vs context"
                            sortKey="liftVsContext"
                            onSortChange={onSortChange}
                        />
                        {showValidation && (
                            <>
                                <SortableHeader
                                    active={sortBy === 'validationRandomEdge'}
                                    className={GROUP_BORDER}
                                    label="Edge vs random"
                                    sortKey="validationRandomEdge"
                                    onSortChange={onSortChange}
                                />
                                <SortableHeader
                                    active={sortBy === 'validationLift'}
                                    label="Lift vs trigger"
                                    sortKey="validationLift"
                                    onSortChange={onSortChange}
                                />
                                <SortableHeader
                                    active={sortBy === 'validationLiftVsContext'}
                                    label="Lift vs context"
                                    sortKey="validationLiftVsContext"
                                    onSortChange={onSortChange}
                                />
                                <SortableHeader
                                    active={sortBy === 'worstWindowEdge'}
                                    className={GROUP_BORDER}
                                    label="Weaker edge"
                                    sortKey="worstWindowEdge"
                                    onSortChange={onSortChange}
                                />
                                <th className={TH} scope="col">
                                    Consistency
                                </th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody className={`divide-y divide-zinc-800 transition-opacity ${isRefreshing ? 'opacity-50' : ''}`}>
                    {isLoading
                        ? Array.from({ length: SKELETON_ROWS }, (_, index) => <SkeletonRow columns={columnCount} key={index} />)
                        : rows.map((row) => {
                              const concentration = describeConcentration(row);
                              const evidenceBadge = describeEvidenceBadge(row);
                              const consistency = describeWindowConsistency(row, validationTwinOf(row));
                              const selected = row.rowFingerprint === selectedRowFingerprint;
                              return (
                                  <tr
                                      aria-selected={selected}
                                      className={`cursor-pointer focus:outline-none focus-visible:bg-zinc-800/60 ${selected ? 'bg-zinc-800/60' : 'hover:bg-zinc-800/40'}`}
                                      key={row.rowFingerprint}
                                      tabIndex={0}
                                      onClick={() => onSelectRow(row)}
                                      onKeyDown={(event) => handleRowKeyDown(event, row)}
                                  >
                                      {cell(
                                          <>
                                              <span className="block font-medium text-zinc-100">{describeCombination(row)}</span>
                                              <span className="block text-xs text-zinc-500">
                                                  {CONDITION_KIND_META[row.conditionKind].label}
                                              </span>
                                          </>,
                                          'min-w-[16rem]'
                                      )}
                                      {cell(
                                          <>
                                              <span className="block text-zinc-200">{row.entityLabel}</span>
                                              <span className="block text-xs text-zinc-500">
                                                  <span className={row.direction === 'long' ? 'text-green-400/80' : 'text-red-400/80'}>
                                                      {alphaAtlasDirectionLabel(outcomeKind, row.direction)}
                                                  </span>
                                                  {' · '}
                                                  {formatHorizon(row.horizonSeconds)}
                                              </span>
                                          </>,
                                          'whitespace-nowrap'
                                      )}
                                      {cell(
                                          <>
                                              <span className="block tabular-nums text-zinc-100">{formatCount(row.supportCount)}</span>
                                              <span className="block text-xs tabular-nums text-zinc-500">
                                                  {formatRatio(row.winRate, 0)} wins · {formatCount(row.distinctEpisodeCount)} effective
                                                  samples
                                              </span>
                                          </>,
                                          'text-right whitespace-nowrap'
                                      )}
                                      {cell(
                                          <div className="flex flex-col items-end">
                                              <Signed micros={row.netMeanMicros} />
                                              <IntervalBar lower={row.ciLowerMicros} scale={intervalScale} upper={row.ciUpperMicros} />
                                              <span className="text-xs tabular-nums text-zinc-500">
                                                  {formatPercentMicros(row.ciLowerMicros, { signed: true })} to{' '}
                                                  {formatPercentMicros(row.ciUpperMicros, { signed: true })}
                                              </span>
                                          </div>,
                                          `text-right whitespace-nowrap ${GROUP_BORDER}`
                                      )}
                                      {cell(
                                          <Signed className="font-semibold" micros={row.randomEdgeMeanMicros} />,
                                          'text-right whitespace-nowrap'
                                      )}
                                      {cell(
                                          row.liftVsTriggerMicros === null ? (
                                              <Blank title="Lift applies to trigger + context rows only" />
                                          ) : (
                                              <div className="flex flex-col items-end">
                                                  <Signed micros={row.liftVsTriggerMicros} />
                                                  <span className="text-xs tabular-nums text-zinc-500">
                                                      trigger alone {formatPercentMicros(row.triggerOnlyNetMeanMicros, { signed: true })}
                                                  </span>
                                              </div>
                                          ),
                                          'text-right whitespace-nowrap'
                                      )}
                                      {cell(
                                          row.liftVsContextMicros === null ? (
                                              <Blank title="Lift applies to trigger + context rows only" />
                                          ) : (
                                              <div className="flex flex-col items-end">
                                                  <Signed micros={row.liftVsContextMicros} />
                                                  <span className="text-xs tabular-nums text-zinc-500">
                                                      context alone {formatPercentMicros(row.contextOnlyNetMeanMicros, { signed: true })}
                                                  </span>
                                              </div>
                                          ),
                                          'text-right whitespace-nowrap'
                                      )}
                                      {showValidation &&
                                          cell(
                                              row.validationEvidenceState === null ? (
                                                  <Blank title="No result for this hypothesis in the selection-validation window" />
                                              ) : (
                                                  <div className="flex flex-col items-end">
                                                      <Signed className="font-semibold" micros={row.validationRandomEdgeMeanMicros} />
                                                      <span
                                                          className={`text-xs tabular-nums ${
                                                              row.validationEvidenceState === 'insufficientSupport'
                                                                  ? 'text-amber-400/80'
                                                                  : 'text-zinc-500'
                                                          }`}
                                                          title={EVIDENCE_STATE_META[row.validationEvidenceState].label}
                                                      >
                                                          n {formatCount(row.validationSupportCount ?? 0)}
                                                      </span>
                                                  </div>
                                              ),
                                              `text-right whitespace-nowrap ${GROUP_BORDER}`
                                          )}
                                      {showValidation &&
                                          cell(
                                              row.validationLiftVsTriggerMicros === null ? (
                                                  <Blank
                                                      title={
                                                          row.conditionKind === 'parentAndChild'
                                                              ? 'No result for this hypothesis in the selection-validation window'
                                                              : 'Lift applies to trigger + context rows only'
                                                      }
                                                  />
                                              ) : (
                                                  <Signed micros={row.validationLiftVsTriggerMicros} />
                                              ),
                                              'text-right whitespace-nowrap'
                                          )}
                                      {showValidation &&
                                          cell(
                                              row.validationLiftVsContextMicros === null ? (
                                                  <Blank
                                                      title={
                                                          row.conditionKind === 'parentAndChild'
                                                              ? 'No result for this hypothesis in the selection-validation window'
                                                              : 'Lift applies to trigger + context rows only'
                                                      }
                                                  />
                                              ) : (
                                                  <Signed micros={row.validationLiftVsContextMicros} />
                                              ),
                                              'text-right whitespace-nowrap'
                                          )}
                                      {showValidation &&
                                          cell(
                                              <Signed className="font-semibold" micros={row.worstWindowRandomEdgeMeanMicros} />,
                                              `text-right whitespace-nowrap ${GROUP_BORDER}`
                                          )}
                                      {showValidation &&
                                          cell(
                                              <Tooltip content={WINDOW_CONSISTENCY_META[consistency].description}>
                                                  <StatusBadge status={consistency} tone={WINDOW_CONSISTENCY_META[consistency].tone}>
                                                      {WINDOW_CONSISTENCY_META[consistency].label}
                                                  </StatusBadge>
                                              </Tooltip>,
                                              'whitespace-nowrap'
                                          )}
                                      {cell(
                                          <div className="flex items-center gap-2">
                                              <Tooltip content={evidenceBadge.description}>
                                                  <StatusBadge status={row.evidenceState} tone={evidenceBadge.tone}>
                                                      {evidenceBadge.label}
                                                  </StatusBadge>
                                              </Tooltip>
                                              {concentration.length > 0 && (
                                                  <Tooltip content={concentration.join('. ')}>
                                                      <FaExclamationTriangle
                                                          aria-label="Concentrated support"
                                                          className="h-3.5 w-3.5 text-amber-400"
                                                      />
                                                  </Tooltip>
                                              )}
                                          </div>,
                                          `whitespace-nowrap ${GROUP_BORDER}`
                                      )}
                                      {cell(<FaChevronRight aria-hidden="true" className="h-3 w-3 text-zinc-600" />, 'text-right')}
                                  </tr>
                              );
                          })}
                </tbody>
            </table>
        </div>
    );
};

export default AlphaAtlasResultsTable;
